/**
 * The ten Asset360 modules, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `ST`        — demo line 2845 (status token → [label, tone] tuple)
 * - `MODULES`   — demo lines 2846–2907 (10 modules)
 * - `STAGE2MOD` — demo line 2909 (legacy seven-stage id → module id)
 * - `AGENT_COL` — demo line 2911 (agent name → swatch)
 *
 * What changed in the port
 * ------------------------
 * 1. **Tuples became named-field interfaces.** `ST` was `{key: [label, tone]}`,
 *    `views`/`items`/`live` were positional arrays. Field names come from the
 *    demo's own consumers: `renderModule` (demo line 3011) and `viewBtn`
 *    (demo line 3017).
 *
 * 2. **Handler strings became typed navigation.** The demo carried navigation
 *    as JavaScript source in data — `"show('machine','L-33');setTimeout(()=>machineTab('record'),40)"`,
 *    `"audit('wos',{wo_type:'Preventive'})"`, `"openDoc('vtt:…')"`, `"ask('…')"`.
 *    None of that survives into React. Every one is now a {@link SeedNav}
 *    discriminated union value. `viewBtn`'s three branches are reproduced
 *    exactly: a bare key `'population'` → `act('population')`, a key with a
 *    colon `'partsflow:predict'` → `act('partsflow','predict')`, and a literal
 *    call → the matching union member. The `setTimeout(()=>machineTab('x'),40)`
 *    tail is carried as the optional `machineTab` field — the 40 ms delay was a
 *    DOM-paint workaround with no meaning in React and is dropped.
 *
 * 3. **`items()` and `live()` were functions over the loaded datasets**
 *    (`FLEET`, `WOS`, `HEALTH`, `B.mx`, `revenue()`, `upgradeCandidates()`…).
 *    Their *shape* is static; only the numbers inside the strings were live. So
 *    each is now plain data, and every `${…}` interpolation is preserved as a
 *    {@link FigureSegment} of the form `{ expr }` holding the demo's expression
 *    verbatim. Nothing is evaluated here and no number is baked in: the scene
 *    lane resolves each `expr` against its own data hook. Literal text around
 *    the interpolations is a `{ text }` segment and is copied character for
 *    character, including `·`, `—` and `→`.
 *
 * 4. **HTML stripped.** None of the module strings carried markup; the
 *    surrounding `<button class="src">`/`<span class="pill">` wrappers lived in
 *    `renderModule`, not in the data, so nothing had to be stripped here.
 *
 * Data Schema layer — pure data and pure types. No React, no side effects.
 */

import { act, type OemAction } from './action';

/* ------------------------------------------------------------------ *
 * Live-figure templates
 * ------------------------------------------------------------------ */

/**
 * One piece of a live figure string.
 *
 * - `{ text }` — literal copy from the demo, verbatim.
 * - `{ expr }` — the demo's interpolated expression, carried as an opaque
 *   string for the scene lane to bind. It is documentation, not code: nothing
 *   in this layer evaluates it.
 */
export type FigureSegment =
  | { readonly text: string }
  | { readonly expr: string };

/** A demo template literal, split into literal text and live expressions. */
export type LiveFigure = readonly FigureSegment[];

/** Literal text segment. */
export const ft = (text: string): FigureSegment => ({ text });

/** Live expression segment, carrying the demo source verbatim. */
export const fx = (expr: string): FigureSegment => ({ expr });

/* ------------------------------------------------------------------ *
 * Machine id aliases
 * ------------------------------------------------------------------ */

/**
 * Keys of the demo's `IDS` map (`const … IDS = B.ids`, demo line 1840).
 *
 * The demo never hard-codes the machine ids it narrates; it looks them up so
 * that regenerating the data pack renumbers every mention at once. Seeds that
 * quote a machine therefore carry the alias, not the id, and the scene lane
 * resolves it against the loaded pack.
 *
 * Shared here because `agents.ts`, `doctree.ts` and `suggestions.ts` all need
 * it, and `modules.ts` is where this seed set keeps its shared primitives.
 */
export const MACHINE_ALIASES = ['L03', 'L14', 'L22', 'L31', 'J07', 'T22'] as const;

/** Closed set of `IDS` keys used anywhere in the demo. */
export type MachineAlias = (typeof MACHINE_ALIASES)[number];

/** The demo's `IDS` map: alias → the machine id in the loaded data pack. */
export type MachineIdMap = Readonly<Record<MachineAlias, string>>;

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

/** One clause of a demo `audit(table, {field: …})` filter object. */
export type AuditFilter =
  | { readonly field: string; readonly op: 'eq'; readonly value: string }
  | { readonly field: string; readonly op: 'startsWith'; readonly value: string }
  /** `value` is a regular-expression source, e.g. `'Warranty'`. */
  | { readonly field: string; readonly op: 'matches'; readonly value: string };

/**
 * Where a module row, view chip or loop row goes when clicked.
 *
 * Replaces the demo's handler-source-in-data. `kind` is the demo function that
 * was called.
 */
export type SeedNav =
  | {
      readonly kind: 'show';
      readonly action: OemAction;
      /** Tail of `setTimeout(()=>machineTab('record'),40)`, if present. */
      readonly machineTab?: 'record' | 'history';
    }
  | { readonly kind: 'audit'; readonly table: string; readonly filter: readonly AuditFilter[] }
  | { readonly kind: 'openDoc'; readonly key: string }
  | { readonly kind: 'ask'; readonly question: string };

/** `show('view')` / `show('view','sub')`. */
export const navShow = (
  view: string,
  sub?: string,
  machineTab?: 'record' | 'history',
): SeedNav =>
  machineTab === undefined
    ? { kind: 'show', action: act(view, sub) }
    : { kind: 'show', action: act(view, sub), machineTab };

/** `audit('table', {…})`. */
export const navAudit = (table: string, filter: readonly AuditFilter[] = []): SeedNav => ({
  kind: 'audit',
  table,
  filter,
});

/** `openDoc('key')`. */
export const navDoc = (key: string): SeedNav => ({ kind: 'openDoc', key });

/** `ask('question')`. */
export const navAsk = (question: string): SeedNav => ({ kind: 'ask', question });

/* ------------------------------------------------------------------ *
 * Status tokens (demo `ST`, line 2845)
 * ------------------------------------------------------------------ */

/** Delivery status of a module item. */
export const MODULE_STATUSES = ['live', 'drafts', 'worked', 'road'] as const;

/** Closed set of module-item statuses. */
export type ModuleStatus = (typeof MODULE_STATUSES)[number];

/** Pill tone used by the demo stylesheet. */
export type StatusTone = 'good' | 'info' | 'accent' | 'mute';

/** The demo's `ST` tuple, as named fields. */
export interface StatusToken {
  /** Human label, verbatim from the demo. */
  readonly label: string;
  /** CSS pill class the demo applied. */
  readonly tone: StatusTone;
}

/** Demo line 2845 — `const ST={…}`. */
export const MODULE_STATUS_TOKENS: Readonly<Record<ModuleStatus, StatusToken>> = {
  live: { label: 'Live', tone: 'good' },
  drafts: { label: 'Drafts for approval', tone: 'info' },
  worked: { label: 'Worked example', tone: 'accent' },
  road: { label: 'Roadmap', tone: 'mute' },
};

/* ------------------------------------------------------------------ *
 * Module records
 * ------------------------------------------------------------------ */

/** A "Related pages elsewhere in the platform" chip. */
export interface ModuleView {
  /** Chip label, verbatim. */
  readonly label: string;
  /** Where the chip goes. */
  readonly nav: SeedNav;
}

/** One row of "What this module does". */
export interface ModuleItem {
  /** What the operating-model poster says. */
  readonly what: string;
  /** Where it lives in the platform. */
  readonly where: string;
  /** The live figure this morning, as a template. */
  readonly figure: LiveFigure;
  /** Where the row goes when clicked. */
  readonly nav: SeedNav;
  /** Delivery status of the row. */
  readonly status: ModuleStatus;
}

/** One KPI tile at the head of a module page. */
export interface ModuleKpi {
  /** The value, as a live-figure template. */
  readonly value: LiveFigure;
  /** The eyebrow under it, verbatim. */
  readonly label: string;
}

/** Legacy seven-stage ids the demo still routes from. */
export const LEGACY_STAGES = [
  'connect',
  'monitor',
  'diagnose',
  'plan',
  'supply',
  'execute',
  'learn',
] as const;

/** Closed set of legacy stage ids. */
export type LegacyStage = (typeof LEGACY_STAGES)[number];

/** Module ids, in poster order. */
export const MODULE_IDS = [
  'installed',
  'demand',
  'orchestrate',
  'diagnose',
  'commercial',
  'promise',
  'parts',
  'capacity',
  'execute',
  'closeout',
] as const;

/** Closed set of module ids. */
export type ModuleId = (typeof MODULE_IDS)[number];

/** One of the ten Asset360 modules. */
export interface OemModule {
  readonly id: ModuleId;
  /** Poster number, 1–10. */
  readonly n: number;
  /** Full name. */
  readonly name: string;
  /** Short name for narrow chrome. */
  readonly short: string;
  /** Headline agent, as printed. */
  readonly agent: string;
  /** Agent roster names (keys into `agents.ts`). */
  readonly agents: readonly string[];
  /** Module swatch. */
  readonly col: string;
  /** Inline SVG path data for the module glyph (viewBox `0 0 24 24`). */
  readonly icon: string;
  /** One-line poster tag. */
  readonly tag: string;
  /** Ids of the legacy aftermarket-wheel capabilities that live here. */
  readonly caps: readonly string[];
  /** The seven-stage id this module replaced. */
  readonly legacy: LegacyStage;
  /** The long-form description. */
  readonly purpose: string;
  /** Connector names feeding the module (keys into `connectors.ts`). */
  readonly systems: readonly string[];
  /** Related-pages chips. */
  readonly views: readonly ModuleView[];
  /** "What this module does" rows — demo `items()`. */
  readonly items: readonly ModuleItem[];
  /** Head KPI tiles — demo `live()`. */
  readonly live: readonly ModuleKpi[];
}

/** Demo lines 2846–2907 — `const MODULES=[…]`, ten records in poster order. */
export const MODULES: readonly OemModule[] = [
  {
    id: 'installed',
    n: 1,
    name: 'Asset Base',
    short: 'Asset base',
    agent: 'Installed Base Agent',
    agents: ['Installed Base', 'Fleet Learning'],
    col: '#2F5FA8',
    icon: '<path d="M3 17h18M5 17V9l4-3h6l4 3v8"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/>',
    tag: 'Every serial number, once, with everything known about it',
    caps: ['population', 'cmms'],
    legacy: 'connect',
    purpose:
      'The foundation of the operating system: one record per machine — customer, site, fleet, machine, component — with its serial numbers and configuration, meters and location, warranty and service contract, the whole maintenance and failure history, parts consumed, life-to-date reliability and what it could still be worth. Meridian\'s Maximo service register holds it; the customers\' own systems stay the systems of record on their side and are cross-referenced, not replaced.',
    systems: [
      'IBM Maximo · service register',
      'IBM Maximo · Gawler Deep EAM',
      'SAP PM',
      'Pitram',
      'RMS',
      'Underground Wi-Fi / LTE telematics',
    ],
    views: [
      { label: 'Population list', nav: navShow('population') },
      { label: 'Sites map', nav: navShow('overview') },
      { label: 'Fleet health', nav: navShow('fleet') },
      { label: 'Machine record · L-18', nav: navShow('machine', 'L-18', 'record') },
      { label: 'Connectors', nav: navShow('connectors') },
    ],
    items: [
      {
        what: 'Customer → Site → Fleet → Machine → Component',
        where: 'Population list · machine page',
        figure: [
          fx('new Set(FLEET.map(f=>f.operated_by)).size'),
          ft(' organisations · '),
          fx('MINE_ORDER.length'),
          ft(' sites · '),
          fx('FLEET.length'),
          ft(' machines · '),
          fx('HEALTH.length'),
          ft(' components'),
        ],
        nav: navShow('population'),
        status: 'live',
      },
      {
        what: 'Serial numbers & configuration',
        where: 'Maximo asset record (class structure, attributes)',
        figure: [
          fx('Object.keys(B.mx).length'),
          ft(' asset records · '),
          fx('Object.keys(CLASSES_).length'),
          ft(' models · drive, size class, operating mode, data mode'),
        ],
        nav: navShow('machine', 'L-33', 'record'),
        status: 'live',
      },
      {
        what: 'Hours, utilisation & location',
        where: 'RMS fleet status · shift records · workings',
        figure: [
          fx('B.rms_status.length'),
          ft(' on RMS with position · '),
          fx('SD.length.toLocaleString()'),
          ft(' machine-days of shift records'),
        ],
        nav: navShow('population'),
        status: 'live',
      },
      {
        what: 'Warranty & service contracts',
        where: 'Maximo contracts on every record',
        figure: [
          fx('B.mx_contracts.length'),
          ft(' site contracts · '),
          fx('warrantyActive().length'),
          ft(' machines under warranty'),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
      {
        what: 'Maintenance & failure history',
        where: 'Machine page · History · Maximo failure reports',
        figure: [
          fx('WOS.length.toLocaleString()'),
          ft(' work orders · '),
          fx('Object.values(B.mx).reduce((a,m)=>a+(m.failurereports||[]).length,0)'),
          ft(' coded failure reports'),
        ],
        nav: navShow('machine', 'L-02', 'history'),
        status: 'live',
      },
      {
        what: 'Parts consumption',
        where: 'Cost summary · ledger by machine',
        figure: [fx('fmtk(revenue().parts18)'), ft(' of parts across the fleet in 18 months')],
        nav: navShow('costs'),
        status: 'live',
      },
      {
        what: 'Lifecycle & reliability',
        where: 'Maximo record · life to date',
        figure: [ft('availability and cost per year since commissioning on every record')],
        nav: navShow('machine', 'L-18', 'record'),
        status: 'live',
      },
      {
        what: 'Revenue potential',
        where: 'Commercial module · white space',
        figure: [
          fx('upgradeCandidates().length'),
          ft(' call-off machines in poor condition or worse — contract upgrade candidates'),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
    ],
    live: [
      { value: [fx('FLEET.length')], label: 'machines registered' },
      { value: [fx('MINE_ORDER.length')], label: 'sites' },
      { value: [fx('B.mx_contracts.length')], label: 'service contracts' },
      { value: [fx('Object.keys(B.rms_health).length')], label: 'on the RMS' },
    ],
  },

  {
    id: 'demand',
    n: 2,
    name: 'Customer & Asset Needs',
    short: 'Customer & asset needs',
    agent: 'Demand Planning Agent',
    agents: ['Demand Planning', 'Diagnostic'],
    col: '#2E7D4F',
    icon: '<path d="M12 20v-6M8 12a6 6 0 018 0M5 8a10 10 0 0114 0"/>',
    tag: 'Where the next job comes from — before anyone raises it',
    caps: ['equipment', 'sos'],
    legacy: 'monitor',
    purpose:
      'Demand for parts and service is sensed from seven directions at once: the PM schedule, the condition of every component, the predictive model, the reactive failures that still happen, the wear parts and consumables, lifecycle events such as rebuilds and upgrades, and the commercial calendar of contracts and opportunities. The morning S.O.S report is what this module says to each site lead.',
    systems: [
      'RMS',
      'Underground Wi-Fi / LTE telematics',
      'Pitram',
      'Oil analysis lab portal',
      'Tyre management',
      'OEM bulletin service',
      'IBM Maximo · service register',
    ],
    views: [
      { label: 'Fleet health', nav: navShow('fleet') },
      { label: 'Alerts', nav: navShow('alerts') },
      { label: 'S.O.S report', nav: navShow('sos') },
      { label: 'Predictive usage', nav: navShow('partsflow', 'predict') },
    ],
    items: [
      {
        what: 'Scheduled maintenance',
        where: 'Maximo PM schedule · SAP PM scheduled work',
        figure: [
          fx('pmDue(14).length'),
          ft(' PMs due in 14 days · '),
          fx('B.mx_pm.length'),
          ft(' PM records with contract vs OEM interval'),
        ],
        nav: navAudit('wos', [
          { field: 'wo_type', op: 'eq', value: 'Preventive' },
          { field: 'status', op: 'eq', value: 'Scheduled' },
        ]),
        status: 'live',
      },
      {
        what: 'Condition monitoring (IoT, telemetry)',
        where: 'Fleet health · condition register',
        figure: [
          fx('HEALTH.filter(h=>ATTN.includes(h.band)).length'),
          ft(' flagged components · '),
          fx("FLEET.filter(f=>MINES[f.mine].data_mode==='Live').length"),
          ft(' machines live · '),
          fx("FLEET.filter(f=>MINES[f.mine].data_mode!=='Live').length"),
          ft(' shift-end'),
        ],
        nav: navShow('fleet'),
        status: 'live',
      },
      {
        what: 'Predictive analytics (AI)',
        where: 'Alerts · failure window and confidence',
        figure: [
          fx('ALERTS.length'),
          ft(' open alerts, each with a failure window, a confidence and the evidence'),
        ],
        nav: navShow('alerts'),
        status: 'live',
      },
      {
        what: 'Reactive failures',
        where: 'Work orders · breakdowns',
        figure: [
          fx("WOS.filter(w=>w.wo_type==='Breakdown'&&w.created>='2026-06-01').length"),
          ft(' breakdowns since June · the trend the model learns from'),
        ],
        nav: navAudit('wos', [{ field: 'wo_type', op: 'eq', value: 'Breakdown' }]),
        status: 'live',
      },
      {
        what: 'Wear & consumables',
        where: 'Tyres · GET · oil · filters',
        figure: [
          fx('B.tyres.rows.filter(r=>r[10]).length'),
          ft(' tyre actions · '),
          fx("B.oil.filter(o=>o.verdict!=='Normal').length"),
          ft(' oil samples on caution or critical'),
        ],
        nav: navAudit('tyres'),
        status: 'live',
      },
      {
        what: 'Lifecycle events (rebuilds, upgrades)',
        where: 'Component lifecycle loop',
        figure: [fx('pastInterval().length'), ft(' components past their change interval')],
        nav: navShow('loops', 'component'),
        status: 'live',
      },
      {
        what: 'Commercial demand (contract, opportunity)',
        where: 'Commercial opportunity loop',
        figure: [
          fx('renewals().filter(r=>r.days<=365).length'),
          ft(' contract renewal inside 12 months · '),
          fx('upgradeCandidates().length'),
          ft(' upgrade candidates'),
        ],
        nav: navShow('loops', 'commercial'),
        status: 'live',
      },
    ],
    live: [
      {
        value: [fx('HEALTH.filter(h=>ATTN.includes(h.band)).length')],
        label: 'flagged components',
      },
      { value: [fx('ALERTS.length')], label: 'open alerts' },
      { value: [fx('pmDue(14).length')], label: 'PMs due in 14 days' },
      { value: [fx('predictedUsage().filter(r=>r.gap>0).length')], label: 'predicted part gaps' },
    ],
  },

  {
    id: 'orchestrate',
    n: 3,
    name: 'Case Management',
    short: 'Case management',
    agent: 'Orchestrator Agent',
    agents: ['Orchestrator'],
    col: '#C8500A',
    icon: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>',
    tag: 'Every event becomes a case with an owner, a priority and a route',
    caps: ['backtrack'],
    legacy: 'diagnose',
    purpose:
      'An alert, a call, a case or an incident enters here. The Orchestrator identifies the asset and the customer, checks what they are entitled to, sets a priority, decides whether the answer is parts, service or a rebuild, routes the work to the specialist agents, tracks it end to end on the job board and escalates to a person the moment a guardrail is reached.',
    systems: [
      'Salesforce',
      'IBM Maximo · service register',
      'Outlook',
      'Microsoft Teams',
    ],
    views: [
      { label: 'Job board', nav: navShow('jobboard') },
      { label: 'Approvals', nav: navShow('approvals') },
      { label: 'Alerts', nav: navShow('alerts') },
      { label: 'Cases', nav: navAudit('cases') },
    ],
    items: [
      {
        what: 'Identify asset & customer',
        where: 'Every alert and case carries the serial, the site and the organisation',
        figure: [
          fx('ALERTS.length'),
          ft(' alerts · '),
          fx('B.cases.length'),
          ft(' cases, all linked to a serial number'),
        ],
        nav: navAudit('cases'),
        status: 'live',
      },
      {
        what: 'Check entitlement (warranty / contract)',
        where: 'Who pays, from the Maximo contract on the record',
        figure: [
          fx('warrantyActive().length'),
          ft(' under warranty · '),
          fx('FLEET.length-warrantyActive().length'),
          ft(' under a site contract'),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
      {
        what: 'Assess risk & priority (P1–P4)',
        where: 'Work-order priority · severity',
        figure: [
          fx("WOS.filter(w=>w.priority.startsWith('1')&&/Draft|Released|Scheduled/.test(w.status)).length"),
          ft(' open P1 · '),
          fx("ALERTS.filter(a=>a.sev==='crit').length"),
          ft(' critical alerts'),
        ],
        nav: navShow('approvals'),
        status: 'live',
      },
      {
        what: 'Determine workflow (parts, service, rebuild)',
        where: 'Job run · generated per job',
        figure: [ft('3 worked runs · every other job generated from its state')],
        nav: navShow('jobrun'),
        status: 'live',
      },
      {
        what: 'Route to specialist agents',
        where: 'Agent chain on every alert',
        figure: [
          fx('ALERTS.reduce((a,x)=>a+x.chain.length,0)'),
          ft(' agent steps on the open alerts'),
        ],
        nav: navShow('alerts'),
        status: 'live',
      },
      {
        what: 'Track progress',
        where: 'Job board · progress ring per job',
        figure: [fx('jobs().length'), ft(' open jobs on the board')],
        nav: navShow('jobboard'),
        status: 'live',
      },
      {
        what: 'Escalate exceptions',
        where: 'Waiting on a person · restrictions',
        figure: [
          fx('jobs().filter(j=>/Approve|approval|Planner/i.test(j.next)).length'),
          ft(' waiting on a person · 1 restriction awaiting approval'),
        ],
        nav: navShow('approvals'),
        status: 'drafts',
      },
    ],
    live: [
      { value: [fx('jobs().length')], label: 'open jobs' },
      { value: [fx("jobs().filter(j=>j.sev==='crit').length")], label: 'critical' },
      {
        value: [fx('jobs().filter(j=>/Approve|approval|Planner/i.test(j.next)).length')],
        label: 'waiting on a person',
      },
      { value: [fx('B.cases.filter(c=>/Open/.test(c.status)).length')], label: 'open cases' },
    ],
  },

  {
    id: 'diagnose',
    n: 4,
    name: 'Assess & Prescribe',
    short: 'Assess & prescribe',
    agent: 'Diagnostic & Knowledge Agents',
    agents: ['Diagnostic', 'Knowledge'],
    col: '#5E35B1',
    icon: '<circle cx="11" cy="11" r="6"/><path d="M16 16l5 5M9 11h4M11 9v4"/>',
    tag: 'From a signal to a finding — with a window, a confidence and a prescription',
    caps: ['safety'],
    legacy: 'diagnose',
    purpose:
      'Telemetry, camera events and the machine\'s own history are analysed together, matched against the knowledge base and the bulletins, and turned into a ranked root cause with a time-to-failure window and a confidence. The prescription follows: the parts, the tools, the labour and the safety steps — and the alternatives, including a remote expert looking through the technician\'s camera before anyone travels.',
    systems: [
      'RMS',
      'XReach',
      'SharePoint',
      'OEM bulletin service',
      'Oil analysis lab portal',
      'CatBase',
    ],
    views: [
      { label: 'Alerts', nav: navShow('alerts') },
      { label: 'Incidents', nav: navShow('incident') },
      { label: 'Remote support', nav: navShow('remote') },
      { label: 'Job cards', nav: navShow('jobcards') },
    ],
    items: [
      {
        what: 'Analyse telemetry, video & history',
        where: 'Alerts · evidence chips',
        figure: [
          fx("FLEET.filter(f=>MINES[f.mine].data_mode==='Live').length"),
          ft(' live feeds · '),
          fx('B.events_daily.rows.reduce((a,r)=>a+r[4],0).toLocaleString()'),
          ft(' alarm events · camera events on the T-09 and L-13 alerts'),
        ],
        nav: navShow('alerts'),
        status: 'live',
      },
      {
        what: 'Match with knowledge base & bulletins',
        where: 'Knowledge articles · bulletin service',
        figure: [
          fx('B.kb.length'),
          ft(' knowledge articles · '),
          fx('B.bulletins.length'),
          ft(' bulletins in force'),
        ],
        nav: navShow('data'),
        status: 'live',
      },
      {
        what: 'Determine root cause',
        where: 'Incidents · attribution',
        figure: [
          fx('INCIDENTS.length'),
          ft(' incidents attributed (practice, inspection standard, contract PM plan) · '),
          fx('WOS.filter(w=>w.root_cause).length'),
          ft(' work orders with a coded root cause'),
        ],
        nav: navShow('incident'),
        status: 'live',
      },
      {
        what: 'Generate prescriptive recommendation (parts, tools, labour, safety)',
        where: 'Job cards',
        figure: [
          fx('B.tapp_jobs.length'),
          ft(' job cards with ranked causes, tools, a quote and the controlled procedure'),
        ],
        nav: navShow('jobcards'),
        status: 'live',
      },
      {
        what: 'Provide alternatives',
        where: 'Remote support · freight options',
        figure: [
          fx('B.xreach_sessions.filter(s=>/Escalated/.test(s.outcome)).length'),
          ft(' remote sessions escalated to a visit · air vs road on every requisition'),
        ],
        nav: navShow('remote'),
        status: 'live',
      },
    ],
    live: [
      { value: [fx('ALERTS.length')], label: 'findings with a window' },
      { value: [fx('INCIDENTS.length')], label: 'incidents replayed' },
      {
        value: [fx("B.xreach_sessions.filter(s=>s.started>='2026-09-01').length")],
        label: 'remote sessions this month',
      },
      { value: [fx('B.kb.length')], label: 'knowledge articles' },
    ],
  },

  {
    id: 'commercial',
    n: 5,
    name: 'Commercial & Entitlement',
    short: 'Commercial & entitlement',
    agent: 'Commercial Agent',
    agents: ['Commercial', 'Warranty & Quality'],
    col: '#2F5FA8',
    icon: '<path d="M5 4h9l5 5v11H5z"/><path d="M14 4v5h5"/><path d="M9 14l2 2 4-4"/>',
    tag: 'Who pays, within what limit, and what else this job could be worth',
    caps: [],
    legacy: 'plan',
    purpose:
      'Before a draft becomes a promise the commercial position is settled: is the machine under warranty or a contract, who pays — the customer, a campaign or goodwill — is the value inside the planner\'s approval limit, and is there an upgrade or additional work the job reveals. The agent drafts the answer on every open job; the Regional Service Manager decides.',
    systems: [
      'IBM Maximo · service register',
      'Salesforce',
      'SAP PM',
      'Customer satisfaction survey',
    ],
    views: [
      { label: 'Customers', nav: navShow('customers') },
      { label: 'Approvals', nav: navShow('approvals') },
      { label: 'Cost summary', nav: navShow('costs') },
      { label: 'Commercial loop', nav: navShow('loops', 'commercial') },
    ],
    items: [
      {
        what: 'Check warranty / contract',
        where: 'Maximo contract and warranty on every record',
        figure: [
          fx('B.mx_contracts.length'),
          ft(' contracts ('),
          fx("[...new Set(B.mx_contracts.map(c=>c.type.split(' — ')[0].split(',')[0]))].join(', ')"),
          ft(') · '),
          fx('warrantyActive().length'),
          ft(' machines under warranty'),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
      {
        what: 'Determine who pays (customer / campaign / goodwill)',
        where: 'Entitlement on every open job (table below)',
        figure: [
          fx('jobs().filter(j=>j.machine).length'),
          ft(' open jobs classified · L-18 hose campaign shared as goodwill · L-02 warranty declined'),
        ],
        nav: navShow('module', 'commercial'),
        status: 'live',
      },
      {
        what: 'Validate approval limits',
        where: 'Approvals · value of the drafts',
        figure: [
          fx("fmt$(WOS.filter(w=>w.status.startsWith('Draft')).reduce((a,w)=>a+w.total_direct_cost_aud,0))"),
          ft(' in drafted work · planner limit A$25k, RSM above'),
        ],
        nav: navShow('approvals'),
        status: 'drafts',
      },
      {
        what: 'Identify commercial opportunities (contract upgrade, additional work)',
        where: 'Commercial opportunity loop',
        figure: [
          fx('upgradeCandidates().length'),
          ft(' upgrade candidates · '),
          fx('renewals().filter(r=>r.days<=365).length'),
          ft(' renewal inside 12 months · '),
          fx('EXCHANGE.length'),
          ft(' exchange units sold this fortnight'),
        ],
        nav: navShow('loops', 'commercial'),
        status: 'live',
      },
    ],
    live: [
      { value: [fx('B.mx_contracts.length')], label: 'contracts' },
      { value: [fx('warrantyActive().length')], label: 'under warranty' },
      {
        value: [fx('B.cases.filter(c=>/Warranty/.test(c.type)).length')],
        label: 'warranty cases',
      },
      { value: [fx('upgradeCandidates().length')], label: 'upgrade candidates' },
    ],
  },

  {
    id: 'promise',
    n: 6,
    name: 'Service Planning',
    short: 'Service planning',
    agent: 'Service Promise Agent',
    agents: ['Service Promise', 'Work Order', 'Service Capacity'],
    col: '#2E7D4F',
    icon: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
    tag: 'Parts, people, tools and the customer\'s window — before anyone drives to site',
    caps: ['jobcards', 'hr', 'notify'],
    legacy: 'plan',
    purpose:
      'A finding becomes a plan the customer can hold Meridian to: parts checked, a technician with the right certificate proposed from the roster, tools and logistics confirmed, the customer\'s own window respected, an executable date calculated — and a service promise issued as a confirmed time window. Every draft waits for a planner; that is the Level-2 guardrail.',
    systems: [
      'IBM Maximo · service register',
      'SAP PM',
      'Rostering / HR',
      'LMS',
      'Outlook',
      'Salesforce',
    ],
    views: [
      { label: 'Approvals', nav: navShow('approvals') },
      { label: 'Technicians', nav: navShow('techs') },
      { label: 'Customers', nav: navShow('customers') },
      { label: 'Job cards', nav: navShow('jobcards') },
    ],
    items: [
      {
        what: 'Check parts availability',
        where: 'Parts position on every alert chain',
        figure: [
          fx("ALERTS.filter(a=>a.chain.some(c=>/Parts/.test(c[0])&&c[2]==='done')).length"),
          ft(' of '),
          fx('ALERTS.length'),
          ft(' alerts with the part reserved or in transit'),
        ],
        nav: navShow('alerts'),
        status: 'live',
      },
      {
        what: 'Check technician availability & skills',
        where: 'Dispatch proposals · roster · LMS',
        figure: [
          fx('dispatchProposals().length'),
          ft(' proposals with a reason · '),
          fx("B.lms.filter(r=>r.status!=='Current'&&r.org==='Meridian Equipment').length"),
          ft(' Meridian certificates to renew'),
        ],
        nav: navShow('techs'),
        status: 'drafts',
      },
      {
        what: 'Confirm tools & logistics',
        where: 'Job cards · tools list · freight',
        figure: [ft('tools per system on every job card · road or air chosen against the window')],
        nav: navShow('jobcards'),
        status: 'live',
      },
      {
        what: 'Validate customer window',
        where: 'Customer notifications · site channel',
        figure: [
          fx('notifications().length'),
          ft(' notifications drafted in each customer\'s channel'),
        ],
        nav: navShow('customers'),
        status: 'drafts',
      },
      {
        what: 'Calculate executable date',
        where: 'Service promise table (below)',
        figure: [fx('promises().length'), ft(' open jobs with an executable date')],
        nav: navShow('module', 'promise'),
        status: 'live',
      },
      {
        what: 'Provide service promise (confirmed time window)',
        where: 'Promise column',
        figure: [
          fx('promises().filter(p=>p.inside).length'),
          ft(' of '),
          fx('promises().length'),
          ft(' promises inside the failure window'),
        ],
        nav: navShow('module', 'promise'),
        status: 'live',
      },
    ],
    live: [
      {
        value: [fx("WOS.filter(w=>w.status.startsWith('Draft')).length")],
        label: 'drafts awaiting approval',
      },
      { value: [fx('dispatchProposals().length')], label: 'dispatch proposals' },
      {
        value: [
          fx('promises().filter(p=>p.inside).length'),
          ft(' / '),
          fx('promises().length'),
        ],
        label: 'promises inside the window',
      },
      { value: [fx('notifications().length')], label: 'notifications drafted' },
    ],
  },

  {
    id: 'parts',
    n: 7,
    name: 'Parts & Supply',
    short: 'Parts & supply',
    agent: 'Parts & Inventory Agent',
    agents: ['Parts & Procurement', 'Inventory Network'],
    col: '#C8500A',
    icon: '<path d="M3 7h13l3 4v6H3z"/><circle cx="7" cy="18" r="2"/><circle cx="16" cy="18" r="2"/><path d="M8 11h5"/>',
    tag: 'The part lands inside the failure window, from the cheapest place it can',
    caps: ['sales', 'allocation', 'inventory'],
    legacy: 'supply',
    purpose:
      'Stock is checked locally, regionally and globally — the site store, the distribution centre, the other DCs and the exchange pool — and the cheapest option that lands inside the window is chosen: transfer, procure, exchange or rebuild. Parts are reserved and kitted against the job, freight is booked to the window, and the network\'s minimum levels are re-planned from predicted usage rather than last year\'s consumption.',
    systems: ['Salesforce', 'CatBase', 'IBM Maximo · service register'],
    views: [
      { label: 'Order pipeline', nav: navShow('partsflow') },
      { label: 'Predictive usage', nav: navShow('partsflow', 'predict') },
      { label: 'Inventory control', nav: navShow('partsflow', 'inventory') },
      { label: 'BOM lookup', nav: navShow('partsflow', 'bom') },
    ],
    items: [
      {
        what: 'Check local, regional, global stock',
        where: 'Inventory snapshot · site store → DC → other DCs',
        figure: [
          fx('[...new Set(B.inventory.map(i=>i.location))].length'),
          ft(' locations · '),
          fx('B.inventory.length'),
          ft(' stock lines · '),
          fx("B.inventory.filter(i=>i.qty_on_hand<i.min_level&&i.location.includes('site')).length"),
          ft(' site-store lines below minimum'),
        ],
        nav: navShow('partsflow', 'inventory'),
        status: 'live',
      },
      {
        what: 'Optimise allocation',
        where: 'Predictive usage → who needs it first',
        figure: [
          fx('predictedUsage().filter(r=>r.gap>0).length'),
          ft(' part lines needed inside a window and not on the site shelf'),
        ],
        nav: navShow('partsflow', 'predict'),
        status: 'live',
      },
      {
        what: 'Transfer / procure / manufacture',
        where: 'Orders · DC transfers · requisitions',
        figure: [
          fx('B.orders.filter(o=>/agent/i.test(o.status)).length'),
          ft(' agent requisitions · '),
          fx('predictedUsage().filter(r=>/Transfer/.test(r.prop)).length'),
          ft(' DC transfers proposed · manufacture on the roadmap'),
        ],
        nav: navShow('partsflow'),
        status: 'drafts',
      },
      {
        what: 'Consider exchange / rebuild',
        where: 'Exchange units on the open jobs',
        figure: [
          fx('EXCHANGE.length'),
          ft(' exchange units in play — hammer, rotary head motor, battery module, turbo'),
        ],
        nav: navShow('loops', 'component'),
        status: 'live',
      },
      {
        what: 'Plan logistics & freight',
        where: 'Air vs road against the window',
        figure: [
          fx('B.orders.filter(o=>/air/i.test(o.status)).length'),
          ft(' on air freight · '),
          fx('fmtk(revenue().freightBd)'),
          ft(' of freight on breakdowns in 18 months'),
        ],
        nav: navShow('partsflow'),
        status: 'live',
      },
      {
        what: 'Reserve and kit parts',
        where: 'Reservations on the alert chains',
        figure: [
          fx('ALERTS.filter(a=>a.chain.some(c=>/reserved|Reserved/.test(c[1]))).length'),
          ft(' reservations against open alerts'),
        ],
        nav: navShow('alerts'),
        status: 'live',
      },
      {
        what: 'Update global inventory plan',
        where: 'Proposed minimums',
        figure: [
          fx('predictedUsage().length'),
          ft(' minimum-level proposals for the coordinator'),
        ],
        nav: navShow('partsflow', 'predict'),
        status: 'drafts',
      },
    ],
    live: [
      {
        value: [fx('B.orders.filter(o=>/agent/i.test(o.status)).length')],
        label: 'agent requisitions',
      },
      { value: [fx('B.orders.filter(o=>/air/i.test(o.status)).length')], label: 'on air freight' },
      {
        value: [
          fx("B.inventory.filter(i=>i.qty_on_hand<i.min_level&&i.location.includes('site')).length"),
        ],
        label: 'site lines below minimum',
      },
      { value: [fx('predictedUsage().filter(r=>r.gap>0).length')], label: 'predicted gaps' },
    ],
  },

  {
    id: 'capacity',
    n: 8,
    name: 'People & Resources',
    short: 'People & resources',
    agent: 'Service Capacity Agent',
    agents: ['Service Capacity'],
    col: '#2F5FA8',
    icon: '<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0112 0"/><circle cx="17" cy="9" r="2.5"/><path d="M14 20a5 5 0 016-4"/>',
    tag: 'The right person, certified, available and close enough',
    caps: ['hr', 'rating'],
    legacy: 'execute',
    purpose:
      'Who is on swing, what they are certified for, where they are, who the customer prefers and who can join remotely — the roster, the LMS, the technician app and XReach answer it. Capacity is balanced against the demand the other modules sense: utilisation, travel, training due, and the remote experts who take the jobs that do not need a truck.',
    systems: ['Rostering / HR', 'LMS', 'Maximo Mobile · technician app', 'XReach'],
    views: [
      { label: 'Technicians', nav: navShow('techs') },
      { label: 'Remote support', nav: navShow('remote') },
      { label: 'Job cards', nav: navShow('jobcards') },
    ],
    items: [
      {
        what: 'Technician availability',
        where: 'Roster W37 · technician app status',
        figure: [
          fx("B.tapp_techs.filter(t=>t.status==='On site').length"),
          ft(' of '),
          fx('B.tapp_techs.length'),
          ft(' technicians on site · '),
          fx('B.tapp_techs.filter(t=>/off roster/.test(t.status)).length'),
          ft(' on R&R'),
        ],
        nav: navShow('techs'),
        status: 'live',
      },
      {
        what: 'Skills & competency',
        where: 'LMS · skills on the roster',
        figure: [
          fx('B.lms.length'),
          ft(' training records · '),
          fx("B.lms.filter(r=>r.status!=='Current').length"),
          ft(' not current'),
        ],
        nav: navAudit('lms'),
        status: 'live',
      },
      {
        what: 'Remote experts',
        where: 'XReach sessions by expert',
        figure: [
          fx('[...new Set(B.xreach_sessions.map(s=>s.expert))].length'),
          ft(' experts · '),
          fx('B.xreach_sessions.length'),
          ft(' sessions · '),
          fx('Math.round(B.xreach_sessions.filter(x=>x.first_time_fix).length/B.xreach_sessions.length*100)'),
          ft('% first-time fix'),
        ],
        nav: navShow('remote'),
        status: 'live',
      },
      {
        what: 'Workshop capacity',
        where: 'Burnie service centre · DC workshops',
        figure: [ft('rebuild queue and bay capacity — on the roadmap')],
        nav: navShow('loops', 'component'),
        status: 'road',
      },
      {
        what: 'Travel & roster',
        where: 'Timesheets · travel avoided',
        figure: [
          fx('B.timesheets.reduce((a,t)=>a+t.travel_hours,0).toFixed(0)'),
          ft(' travel hours in W36 · '),
          fx('B.xreach_sessions.reduce((a,s)=>a+s.travel_avoided_km,0).toLocaleString()'),
          ft(' km avoided by remote support'),
        ],
        nav: navAudit('timesheets'),
        status: 'live',
      },
      {
        what: 'Training requirements',
        where: 'Certificates to renew',
        figure: [
          fx("B.lms.filter(r=>r.status!=='Current'&&r.org==='Meridian Equipment').length"),
          ft(' Meridian certificates expiring or expired'),
        ],
        nav: navShow('techs'),
        status: 'live',
      },
      {
        what: 'Balance global demand and capacity',
        where: 'Utilisation vs open jobs',
        figure: [
          fx('meanUtil()'),
          ft('% mean technician utilisation · '),
          fx("B.tapp_jobs.filter(j=>j.status!=='Completed').length"),
          ft(' open jobs'),
        ],
        nav: navShow('techs'),
        status: 'live',
      },
    ],
    live: [
      {
        value: [
          fx("B.tapp_techs.filter(t=>t.status==='On site').length"),
          ft(' / '),
          fx('B.tapp_techs.length'),
        ],
        label: 'technicians on site',
      },
      { value: [fx('meanUtil()'), ft('%')], label: 'mean utilisation' },
      {
        value: [fx("B.lms.filter(r=>r.status!=='Current'&&r.org==='Meridian Equipment').length")],
        label: 'certificates to renew',
      },
      { value: [fx('B.xreach_sessions.length')], label: 'remote sessions' },
    ],
  },

  {
    id: 'execute',
    n: 9,
    name: 'Execution & Support',
    short: 'Execution & support',
    agent: 'Technician Copilot & Safety Agent',
    agents: ['Technician Copilot', 'Safety & Compliance'],
    col: '#5E35B1',
    icon: '<circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.2 2.2M16.2 16.2l2.2 2.2M5.6 18.4l2.2-2.2M16.2 7.8l2.2-2.2"/>',
    tag: 'The work package, the copilot, the expert and the safety check in the technician\'s hand',
    caps: ['backtrack', 'safety'],
    legacy: 'execute',
    purpose:
      'The technician arrives with the work package already assembled — the steps, the torques, the parts, the isolation — carried on the Maximo Mobile app and read hands-free on the headset. A remote expert joins through the camera when the job needs one; the safety agent confirms isolation and permits before work starts; the repair is tested, the machine returned to service and every photo, reading and sign-off captured as it happens.',
    systems: [
      'Maximo Mobile · technician app',
      'XReach',
      'SharePoint',
      'Training video & AR headset',
      'IBM Maximo · service register',
    ],
    views: [
      { label: 'Job cards', nav: navShow('jobcards') },
      { label: 'Remote support', nav: navShow('remote') },
      { label: 'Job run', nav: navShow('jobrun') },
      { label: 'Technicians', nav: navShow('techs') },
    ],
    items: [
      {
        what: 'Prepare work package',
        where: 'Job card · procedure · parts · tools',
        figure: [
          fx("B.tapp_jobs.filter(j=>j.status!=='Completed').length"),
          ft(' open work packages on the technician app'),
        ],
        nav: navShow('jobcards'),
        status: 'live',
      },
      {
        what: 'Technician copilot (AR / mobile)',
        where: 'Maximo Mobile · headset transcript',
        figure: [
          fx("B.tapp_jobs.filter(j=>j.status==='In progress').length"),
          ft(' jobs in progress · 66-minute headset transcript of the L-02 coupling job'),
        ],
        nav: navDoc('vtt:AR-KG-20260903-DW.vtt'),
        status: 'worked',
      },
      {
        what: 'Remote expert support',
        where: 'XReach',
        figure: [
          fx("B.xreach_sessions.filter(s=>s.started>='2026-09-01').length"),
          ft(' sessions this month · '),
          fx('B.kb.length'),
          ft(' became knowledge articles'),
        ],
        nav: navShow('remote'),
        status: 'live',
      },
      {
        what: 'Safety & compliance checks',
        where: 'Restrictions and holds in force',
        figure: [
          ft(
            '4 restrictions in force (L-07, L-33, S-09, bay 2) · 1 awaiting approval (T-09) · LOTO-UG-003 on every underground job',
          ),
        ],
        nav: navShow('issues'),
        status: 'live',
      },
      {
        what: 'Execute repair / rebuild',
        where: 'Labour lines on the technician app',
        figure: [
          fx("B.tapp_jobs.filter(j=>j.status==='Completed').length"),
          ft(' jobs completed in ten weeks'),
        ],
        nav: navAudit('xreach', [{ field: 'status', op: 'eq', value: 'Completed' }]),
        status: 'live',
      },
      {
        what: 'Test and return to service',
        where: 'Checklists · post-repair tests',
        figure: [
          ft(
            'post-repair test on every completed job card · L-18 heat-soak test before RTS 09 Sep 05:40',
          ),
        ],
        nav: navShow('incident', 'IR-2026-015'),
        status: 'live',
      },
      {
        what: 'Capture evidence (photos, data, confirmation)',
        where: 'Checklists, readings and photos on the technician app',
        figure: [
          fx('B.tapp_jobs.reduce((a,j)=>a+(j.labour_lines||[]).length,0)'),
          ft(' labour lines and '),
          fx('B.tapp_jobs.filter(j=>j.customer_signoff).length'),
          ft(' customer sign-offs captured on the app'),
        ],
        nav: navShow('jobcards'),
        status: 'live',
      },
    ],
    live: [
      {
        value: [fx("B.tapp_jobs.filter(j=>j.status==='In progress').length")],
        label: 'jobs in progress',
      },
      {
        value: [
          fx('Math.round(B.xreach_sessions.filter(x=>x.first_time_fix).length/B.xreach_sessions.length*100)'),
          ft('%'),
        ],
        label: 'first-time fix',
      },
      { value: [ft('5')], label: 'restrictions & holds' },
      {
        value: [fx('B.tapp_jobs.filter(j=>j.customer_signoff).length')],
        label: 'customer sign-offs',
      },
    ],
  },

  {
    id: 'closeout',
    n: 10,
    name: 'Closeout & Value',
    short: 'Closeout & value',
    agent: 'Work Order Agent',
    agents: ['Work Order', 'Fleet Learning'],
    col: '#2E7D4F',
    icon: '<path d="M5 12l5 5L20 7"/>',
    tag: 'Every job closed properly is the next job made cheaper',
    caps: ['rating', 'css'],
    legacy: 'learn',
    purpose:
      'Close-out is where the value is captured: the work order completed with parts consumed, labour hours, component serials and a coded root cause; the customer signs off and rates the job; the invoice or the warranty claim is raised; and the follow-up work is identified before the truck leaves. What is learned flows into the five feedback loops.',
    systems: [
      'IBM Maximo · service register',
      'SAP PM',
      'Customer satisfaction survey',
      'Microsoft Teams',
    ],
    views: [
      { label: 'Cost summary', nav: navShow('costs') },
      { label: 'Customers · CSS', nav: navShow('customers') },
      { label: 'Incidents', nav: navShow('incident') },
      { label: 'Feedback loops', nav: navShow('loops') },
    ],
    items: [
      {
        what: 'Complete work order',
        where: 'Closed work orders',
        figure: [
          fx("WOS.filter(w=>/^Closed/.test(w.status)&&w.created>='2026-09-01').length"),
          ft(' closed this month · '),
          fx('WOS.filter(w=>/^Closed/.test(w.status)).length.toLocaleString()'),
          ft(' in the register'),
        ],
        nav: navAudit('wos', [{ field: 'status', op: 'startsWith', value: 'Closed' }]),
        status: 'live',
      },
      {
        what: 'Parts consumed',
        where: 'Parts used on every work order and job card',
        figure: [
          fx('WOS.filter(w=>w.parts_used).length.toLocaleString()'),
          ft(' work orders with parts consumed'),
        ],
        nav: navShow('costs'),
        status: 'live',
      },
      {
        what: 'Labour hours',
        where: 'Timesheets · labour lines',
        figure: [
          fx('B.timesheets.reduce((a,t)=>a+t.job_hours,0).toFixed(0)'),
          ft(' job hours in W36 across '),
          fx('B.tapp_techs.length'),
          ft(' technicians'),
        ],
        nav: navAudit('timesheets'),
        status: 'live',
      },
      {
        what: 'Component serials',
        where: 'Maximo sub-assets with install dates',
        figure: [
          fx('Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)'),
          ft(' serialised sub-assets tracked'),
        ],
        nav: navShow('machine', 'L-18', 'record'),
        status: 'live',
      },
      {
        what: 'Root cause classification',
        where: 'Maximo failure reports (problem / cause / remedy)',
        figure: [
          fx('Object.values(B.mx).reduce((a,m)=>a+(m.failurereports||[]).length,0)'),
          ft(' coded failure reports · '),
          fx('INCIDENTS.length'),
          ft(' incident attributions'),
        ],
        nav: navShow('incident'),
        status: 'live',
      },
      {
        what: 'Customer sign-off',
        where: 'Technician app sign-off · CSS',
        figure: [
          fx('B.tapp_jobs.filter(j=>j.customer_signoff).length'),
          ft(' sign-offs · '),
          fx('B.css.rows.length'),
          ft(' CSS responses this month'),
        ],
        nav: navShow('customers'),
        status: 'live',
      },
      {
        what: 'Invoice / warranty claim',
        where: 'Warranty cases · invoicing',
        figure: [
          fx('B.cases.filter(c=>/Warranty/.test(c.type)).length'),
          ft(
            ' warranty cases (1 declined, 1 under assessment) · invoicing from the close-out on the roadmap',
          ),
        ],
        nav: navAudit('cases', [{ field: 'type', op: 'matches', value: 'Warranty' }]),
        status: 'drafts',
      },
      {
        what: 'Identify follow-up work',
        where: 'Follow-ups on the record',
        figure: [
          ft(
            'L-02 oil sample at +50 h · L-18 heat-affected cabling at the next PM-250 · thread-gauge at every PM-250 on MS-series',
          ),
        ],
        nav: navShow('jobboard'),
        status: 'live',
      },
    ],
    live: [
      {
        value: [fx("WOS.filter(w=>/^Closed/.test(w.status)&&w.created>='2026-09-01').length")],
        label: 'closed this month',
      },
      {
        value: [
          fx('(B.css.rows.reduce((a,r)=>a+r[10],0)/B.css.rows.length).toFixed(1)'),
          ft(' / 5'),
        ],
        label: 'customer rating',
      },
      {
        value: [fx('B.cases.filter(c=>/Warranty/.test(c.type)).length')],
        label: 'warranty cases',
      },
      { value: [fx('FLEET_RULES.length')], label: 'fleet rules from close-outs' },
    ],
  },
];

/** `MODULES` keyed by id — the demo's `MOD_BY` (line 2908). */
export const MODULE_BY_ID: Readonly<Record<ModuleId, OemModule>> = Object.fromEntries(
  MODULES.map((m) => [m.id, m]),
) as Record<ModuleId, OemModule>;

/**
 * Demo line 2909 — `const STAGE2MOD={…}`.
 *
 * The demo still accepts the legacy seven-stage route ids and redirects them to
 * the module that absorbed each stage.
 */
export const STAGE_TO_MODULE: Readonly<Record<LegacyStage, ModuleId>> = {
  connect: 'installed',
  monitor: 'demand',
  diagnose: 'diagnose',
  plan: 'promise',
  supply: 'parts',
  execute: 'execute',
  learn: 'closeout',
};

/**
 * Demo line 2911 — `const AGENT_COL={…}`.
 *
 * Kept here rather than in `agents.ts` because it is module-palette metadata:
 * each agent is tinted with the colour of the module column it belongs to.
 * `renderAgents` (demo line 2514) and `renderModule` (demo line 3011) both read
 * it, falling back to `var(--accent)` for a name that is missing.
 */
export const AGENT_COLOURS: Readonly<Record<string, string>> = {
  Orchestrator: '#C8500A',
  Diagnostic: '#5E35B1',
  Knowledge: '#5E35B1',
  'Parts & Procurement': '#C8500A',
  'Work Order': '#2E7D4F',
  'Technician Copilot': '#5E35B1',
  'Safety & Compliance': '#2F5FA8',
  'Fleet Learning': '#5E35B1',
  'Installed Base': '#2F5FA8',
  'Demand Planning': '#2E7D4F',
  'Inventory Network': '#2F5FA8',
  'Service Capacity': '#2F5FA8',
  'Service Promise': '#2E7D4F',
  'Component Lifecycle': '#2F5FA8',
  'Warranty & Quality': '#2F5FA8',
  Commercial: '#2F5FA8',
};
